import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Smartphone, Tablet, Watch, ShieldCheck, Sparkles, Zap } from "lucide-react";
import { fetchCategories } from "@/lib/api";
import { HOME } from "@/constants/testIds";

const CATEGORY_ICON = {
  smartphone: Smartphone,
  tablette: Tablet,
  "montre-connectee": Watch,
};

const CATEGORY_SUBTITLE = {
  smartphone: "Apple, Samsung, Xiaomi, Google…",
  tablette: "iPad, Galaxy Tab, Surface…",
  "montre-connectee": "Apple Watch, Garmin, Galaxy Watch…",
};

const CategoryCard = ({ category, index }) => {
  return (
    <Link
      to={`/vendre?categorie=${category.slug}`}
      data-testid={HOME.category(category.slug)}
      className="gold-frame group relative rounded-2xl bg-[hsl(var(--bg-elev))]/70 px-7 py-9 flex flex-col gap-5 mp-fade-up overflow-hidden"
      style={{ animationDelay: `${120 + index * 90}ms` }}
    >
      <div className="absolute -right-10 -top-10 size-40 rounded-full bg-[hsl(var(--gold))]/[0.05] blur-2xl pointer-events-none transition-opacity duration-500 group-hover:opacity-100 opacity-50" />
      <div className="flex items-start justify-between">
        <div className="size-20 md:size-24 rounded-xl bg-[hsl(var(--bg))] border border-[hsl(var(--border-soft))] grid place-items-center overflow-hidden group-hover:border-[hsl(var(--gold))]/60 transition-colors">
          {category.hero_image_url ? (
            <img
              src={category.hero_image_url}
              alt={category.label}
              className="w-full h-full object-contain p-2"
              loading="lazy"
            />
          ) : null}
        </div>
        <span className="text-[11px] uppercase tracking-[0.25em] text-[hsl(var(--text-dim))] mt-2">
          {category.model_count}&nbsp;modèles
        </span>
      </div>
      <div className="flex-1">
        <h3 className="font-display text-2xl tracking-wide text-[hsl(var(--text))] mb-2">
          {category.label}
        </h3>
        <p className="text-sm text-[hsl(var(--text-muted))] leading-relaxed">
          {CATEGORY_SUBTITLE[category.slug] || ""}
        </p>
      </div>
      <div className="flex items-center justify-between mt-2">
        <span
          data-testid={HOME.categoryStart(category.slug)}
          className="text-sm font-medium text-gold-bright group-hover:text-gold transition-colors"
        >
          Démarrer →
        </span>
        <span className="text-xs text-[hsl(var(--text-dim))]">
          Estimation gratuite
        </span>
      </div>
    </Link>
  );
};

const TrustBadge = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 p-5 rounded-xl border border-[hsl(var(--border-soft))]/70 bg-[hsl(var(--bg-elev))]/40">
    <div className="size-10 grid place-items-center rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border-soft))] text-gold-bright shrink-0">
      <Icon size={18} strokeWidth={1.6} />
    </div>
    <div>
      <div className="text-sm font-medium text-[hsl(var(--text))]">{title}</div>
      <div className="text-xs text-[hsl(var(--text-muted))] mt-1">{subtitle}</div>
    </div>
  </div>
);

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories()
      .then(setCategories)
      .catch(() => setCategories([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="container-mp pt-16 md:pt-24 pb-12" data-testid={HOME.hero}>
      {/* Hero */}
      <div className="grid md:grid-cols-[1.1fr,0.9fr] gap-12 items-end mb-16 md:mb-24">
        <div className="mp-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/[0.07] text-xs tracking-[0.2em] uppercase text-gold-bright mb-6">
            <Sparkles size={12} /> Prix officiels CompaRecycle
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
            <span className="text-[hsl(var(--text))]">Votre appareil</span>
            <br />
            <span className="text-gold-bright">vaut de l’or.</span>
          </h1>
          <p className="mt-7 text-base md:text-lg text-[hsl(var(--text-muted))] max-w-xl leading-relaxed">
            Devis intelligent en moins d’une minute, puis finalisation directe et
            sécurisée sur CompaRecycle. Aucune création de compte. Aucune
            paperasse. Juste votre précieux, et sa juste valeur.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link to="/vendre" className="btn-gold" data-testid={HOME.startBtn}>
              Vendre un appareil →
            </Link>
            <Link to="/faq" className="btn-ghost">
              Comment ça marche ?
            </Link>
          </div>
        </div>
        <div className="hidden md:block mp-fade-up" style={{ animationDelay: "240ms" }}>
          <div className="relative aspect-[5/4] rounded-2xl overflow-hidden border border-[hsl(var(--border-soft))] bg-[hsl(var(--bg-elev))]">
            <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--gold))]/[0.08] via-transparent to-[hsl(var(--gold-deep))]/20" />
            {/* Citation centered vertically and horizontally */}
            <div className="absolute inset-0 grid place-items-center px-8 z-10">
              <div className="font-serif italic text-2xl md:text-[26px] lg:text-[30px] text-[hsl(var(--text))]/95 text-center leading-[1.22]">
                «&nbsp;Un seul site pour les estimer tous,<br />
                un seul site pour les revendre.&nbsp;»
              </div>
            </div>
            {/* Bottom-right cluster: ring sitting just above the signature */}
            <div className="absolute bottom-4 right-5 flex flex-col items-end gap-3 z-20">
              {/* Warm ember halo under the ring */}
              <div className="relative mr-12">
                <div
                  className="absolute inset-0 -m-3 rounded-full pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(closest-side, rgba(255,170,80,0.32), rgba(255,170,80,0.08) 50%, transparent 75%)",
                    filter: "blur(8px)",
                  }}
                />
                <div className="mp-ring-spin relative">
                  <img
                    src="/one-ring.png"
                    alt="L'Anneau Unique — Mon Précieux"
                    className="w-[81px] lg:w-[92px] h-auto mp-ring-float select-none"
                    draggable={false}
                    style={{
                      filter:
                        "drop-shadow(0 0 14px rgba(255,170,80,0.55)) drop-shadow(0 10px 18px rgba(0,0,0,0.55))",
                    }}
                  />
                </div>
              </div>
              <div className="italic font-semibold tracking-[0.2em] text-sm text-gold-bright/85">
                ◆ Mon Précieux ◆
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Categories */}
      <section className="mb-24">
        <div className="flex items-end justify-between mb-7">
          <h2 className="font-display text-2xl md:text-3xl tracking-wide">
            Que voulez-vous vendre&nbsp;?
          </h2>
          <span className="hidden sm:inline text-xs uppercase tracking-[0.25em] text-[hsl(var(--text-dim))]">
            Sélectionnez une catégorie
          </span>
        </div>
        {loading ? (
          <div className="grid md:grid-cols-3 gap-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="mp-shimmer h-48 rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-5">
            {categories.map((c, i) => (
              <CategoryCard key={c.slug} category={c} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* Trust */}
      <section className="mb-12" data-testid={HOME.trustBadges}>
        <div className="grid md:grid-cols-3 gap-4">
          <TrustBadge
            icon={Zap}
            title="En moins d’une minute"
            subtitle="Estimation immédiate basée sur les prix CompaRecycle."
          />
          <TrustBadge
            icon={ShieldCheck}
            title="100% sécurisé"
            subtitle="Finalisation directe sur la plateforme officielle CompaRecycle."
          />
          <TrustBadge
            icon={Sparkles}
            title="Sans compte"
            subtitle="Aucune inscription, aucune donnée superflue. Juste un devis."
          />
        </div>
      </section>
    </div>
  );
}
